import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Sparkles, Check, X, ChevronRight } from 'lucide-react'
import { reconciliationApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import type { RuleSuggestion } from '@/types'
import { useNavigate } from 'react-router-dom'

interface RuleSuggestionToastProps {
  suggestion: RuleSuggestion
  onDismiss?: () => void
}

/**
 * A toast-like component that shows a rule suggestion from AI
 * Can be used inline or as a floating notification
 */
export function RuleSuggestionToast({ suggestion, onDismiss }: RuleSuggestionToastProps) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const acceptMutation = useMutation({
    mutationFn: () => reconciliationApi.ruleSuggestions.accept(suggestion.suggestion_id),
    onSuccess: (rule) => {
      queryClient.invalidateQueries({ queryKey: ['rule-suggestions'] })
      queryClient.invalidateQueries({ queryKey: ['rule-suggestions-count'] })
      queryClient.invalidateQueries({ queryKey: ['reconciliation-rules'] })
      toast.success(`Rule "${rule.rule_name}" created successfully!`)
      onDismiss?.()
    },
    onError: () => toast.error('Failed to create rule'),
  })

  const dismissMutation = useMutation({
    mutationFn: () => reconciliationApi.ruleSuggestions.dismiss(suggestion.suggestion_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rule-suggestions'] })
      queryClient.invalidateQueries({ queryKey: ['rule-suggestions-count'] })
      onDismiss?.()
    },
    onError: () => toast.error('Failed to dismiss suggestion'),
  })

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 p-2 rounded-full bg-primary/10">
            <Sparkles className="h-4 w-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="font-medium text-sm">AI Suggestion</p>
              {suggestion.confidence_score && (
                <Badge variant="outline" className="text-xs">
                  {(suggestion.confidence_score > 1 ? suggestion.confidence_score : suggestion.confidence_score * 100).toFixed(0)}% confident
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mb-2">
              Create rule: <span className="font-medium text-foreground">{suggestion.suggested_name}</span>
            </p>
            {suggestion.ai_reasoning && (
              <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
                {suggestion.ai_reasoning}
              </p>
            )}
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => acceptMutation.mutate()}
                disabled={acceptMutation.isPending || dismissMutation.isPending}
              >
                <Check className="h-3.5 w-3.5 mr-1" />
                Create Rule
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => dismissMutation.mutate()}
                disabled={acceptMutation.isPending || dismissMutation.isPending}
              >
                <X className="h-3.5 w-3.5 mr-1" />
                Not Now
              </Button>
              <Button
                size="sm"
                variant="link"
                className="ml-auto"
                onClick={() => navigate('/reconciliation/rule-suggestions')}
              >
                View All
                <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Hook to show rule suggestion count badge
 */
export function useRuleSuggestionCount() {
  const { data } = useQuery({
    queryKey: ['rule-suggestions-count'],
    queryFn: () => reconciliationApi.ruleSuggestions.getPendingCount(),
    refetchInterval: 60000, // Refetch every minute
  })
  return data?.count || 0
}

export default RuleSuggestionToast

